import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { FiClock, FiExternalLink, FiFilter, FiSearch, FiChevronLeft, FiChevronRight, FiAward } from 'react-icons/fi';
import DOMPurify from 'dompurify';
import deploymentConfig from '../config/deployment';

const NFTMintingHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    blockchain: '',
    network: '',
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch minted NFTs with pagination and filters
  const { data: mintingData, isLoading, error } = useQuery(
    ['minted-nfts', currentPage, filters, searchTerm],
    () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      return fetch(deploymentConfig.getApiUrl(`nft-minting/minted?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}`
        }
      }).then(res => res.json());
    },
    { 
      keepPreviousData: true,
      staleTime: 30000 
    }
  );

  const nfts = mintingData?.nfts || [];
  const pagination = mintingData?.pagination || {};

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'minted': 'bg-green-100 text-green-800',
      'transferred': 'bg-primary-100 text-primary-800', 
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const truncateAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <FiFilter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select
            value={filters.blockchain}
            onChange={(e) => setFilters(prev => ({ ...prev, blockchain: e.target.value }))}
            className="text-sm border rounded px-3 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Blockchains</option>
            <option value="algorand">Algorand</option>
            {/* MULTI-CHAIN SUPPORT DISABLED - ALGORAND ONLY */}
            {/* <option value="solana">Solana</option> */}
            {/* <option value="polygon">Polygon</option> */}
          </select>
          
          <select
            value={filters.network}
            onChange={(e) => setFilters(prev => ({ ...prev, network: e.target.value }))}
            className="text-sm border rounded px-3 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Networks</option>
            <option value="testnet">TestNet</option>
            <option value="mainnet">MainNet</option>
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="text-sm border rounded px-3 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="minted">Minted</option>
            <option value="transferred">Transferred</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          
          {(filters.blockchain || filters.network || filters.status) && (
            <button
              onClick={() => setFilters({ blockchain: '', network: '', status: '' })}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by recipient address, transaction ID, or template name (not active)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* NFT Minting History Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            NFT Minting History
          </h3>
          <div className="text-sm text-gray-600">
            {pagination.totalItems || 0} total mints
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse border rounded-lg p-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="w-20 h-6 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-600 py-8">
            <p>Error loading minting history</p>
            <p className="text-sm mt-2">{error.message}</p>
          </div>
        ) : nfts.length > 0 ? (
          <div className="space-y-4">
            {nfts.map((nft) => (
              <div key={nft.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {/* NFT Preview - Larger with Arweave thumbnail support */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gradient-to-br from-primary-500 to-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0 shadow-md">
                      {nft.metadata?.image ? (
                        nft.metadata.image.startsWith('data:image/svg+xml') ? (
                          <div
                            className="w-full h-full"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize((() => {
                                try {
                                  return decodeURIComponent(nft.metadata.image.replace('data:image/svg+xml,', ''));
                                } catch (e) {
                                  return nft.metadata.image.replace('data:image/svg+xml,', '');
                                }
                              })(), { USE_PROFILES: { svg: true } })
                            }}
                          />
                        ) : (
                          <a
                            href={nft.metadata.image_large || nft.arweave_image_url || nft.metadata.image}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Click to view full image"
                          >
                            <img
                              src={nft.metadata.image}
                              alt={nft.template_name || 'NFT'}
                              className="w-full h-full object-cover hover:opacity-90 transition-opacity cursor-pointer"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = nft.template_name ? nft.template_name.charAt(0).toUpperCase() : '🎨';
                              }}
                            />
                          </a>
                        )
                      ) : (
                        <span className="text-2xl">
                          {nft.template_name ? nft.template_name.charAt(0).toUpperCase() : '🎨'}
                        </span>
                      )}
                    </div>
                    
                    {/* NFT Details */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-gray-900">
                          {nft.achievement_data?.title ? (
                            <>
                              <span className="font-bold">{nft.achievement_data.title}</span>
                              {nft.template_name && (
                                <span className="font-normal text-gray-600"> - {nft.template_name}</span>
                              )}
                            </>
                          ) : (
                            <span className="font-medium">{nft.template_name || 'Unknown Template'}</span>
                          )}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(nft.mint_status)}`}>
                          {nft.mint_status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Recipient:</span>{' '}
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {truncateAddress(nft.recipient_address)}
                          </code>
                        </p>
                        
                        <p>
                          <span className="font-medium">Network:</span> {nft.blockchain} ({nft.network})
                        </p>
                        
                        <div className="flex items-center space-x-4">
                          <p className="flex items-center">
                            <FiClock className="w-3 h-3 mr-1" />
                            {formatDate(nft.created_at)}
                          </p>
                          
                          {nft.created_by_name && (
                            <p>
                              <span className="font-medium">By:</span> {nft.created_by_name}
                              {nft.mint_source && (
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                  nft.mint_source === 'user'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {nft.mint_source === 'user' ? 'User Mint' : 'Admin Mint'}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        
                        {/* Achievement Info */}
                        {nft.achievement_data && Object.keys(nft.achievement_data).length > 0 && (
                          <div className="mt-2 p-2 bg-primary-50 rounded border-l-4 border-primary-400">
                            <div className="flex items-center text-primary-700">
                              <FiAward className="w-4 h-4 mr-2" />
                              <span className="font-medium">
                                {nft.achievement_data.title || 'Achievement NFT'}
                              </span>
                              {nft.achievement_data.points && (
                                <span className="ml-2 text-primary-600">
                                  ({nft.achievement_data.points} points)
                                </span>
                              )}
                              {nft.achievement_data.difficulty && (
                                <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded">
                                  {nft.achievement_data.difficulty}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2">
                    {nft.txn_id && (
                      <a
                        href={`https://testnet.explorer.perawallet.app/tx/${nft.txn_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        View Transaction
                      </a>
                    )}
                    
                    {nft.asset_id && (
                      <a
                        href={`https://testnet.explorer.perawallet.app/asset/${nft.asset_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-green-600 hover:text-green-800"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        View NFT Asset
                      </a>
                    )}

                    {nft.arweave_metadata_url && (
                      <a
                        href={nft.arweave_metadata_url.replace('#arc3', '')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        View Arweave Metadata
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FiAward className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No NFTs minted yet</h3>
            <p className="text-gray-500 mt-1">
              NFTs minted through the system will appear here
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <div className="text-sm text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages} 
              ({pagination.totalItems} total items)
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pagination.hasPrev}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!pagination.hasNext}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <FiChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NFTMintingHistory;